<section class="public-references" aria-label="Public reference configuration" tabindex="-1">
  <div class="public-references__location">
    <p>Issuing location: <strong>{{ issuingLocationLabel }}</strong></p>
    <button uiButton type="button" [text]="true" [outlined]="false"
      [disabled]="interactionBlocked" (click)="openGeneral()">Change in General</button>
  </div>

  <p *ngIf="!compatible" class="public-references__notice" role="status">
    This saved format does not support editing public references. Pin the draft's capabilities before configuring numbering; retained settings remain unchanged.
  </p>

  <div *ngIf="!declarationSupported" class="public-references__notice" role="status">
    <strong>Retained public reference settings</strong>
    <p>This declaration contains settings this editor does not support. The original configuration is retained for inspection and cannot be rewritten here.</p>
    <details><summary>Saved configuration</summary><pre>{{ document?.numbering | json }}</pre></details>
  </div>
  <p *ngIf="changeError()" class="public-references__notice" role="alert">{{ changeError() }}</p>

  <fieldset class="public-references__choice">
    <legend>Reference for new runs</legend>
    <label *ngIf="!hearing" class="public-references__mode">
      <input uiControl type="radio" name="workflow-public-reference-mode" value="disabled"
        [checked]="declarationSupported && mode === 'disabled'" [disabled]="authoringDisabled"
        (change)="change({ mode: 'disabled' })" />
      <span><strong>No reference</strong><small>New runs will not receive a public reference.</small></span>
    </label>
    <label class="public-references__mode">
      <input uiControl type="radio" name="workflow-public-reference-mode" value="required"
        [checked]="declarationSupported && (hearing || mode === 'required')" [disabled]="authoringDisabled || hearing"
        (change)="change({ mode: 'required' })" />
      <span><strong>Require a reference for new runs</strong><small>A valid reference is required before a new run can start.</small></span>
    </label>

    <div *ngIf="(hearing || mode === 'required') && declarationSupported" class="public-references__required">
      <div *ngIf="!issuingScopeSupported" class="public-references__notice" role="status" data-testid="numbering-scope-prerequisite">
        <strong>Choose a supported issuing location</strong>
        <p>Required references need an issuing location other than All locations and a matching published policy.</p>
        <button uiButton type="button" [disabled]="interactionBlocked" (click)="openGeneral()">Open General settings</button>
        <p>Your reference choice, saved policy and context codes are kept while you choose a location. Incomplete settings can still be saved as an unvalidated draft.</p>
      </div>

      <ng-container *ngIf="issuingScopeSupported">
        <div *ngIf="hearing && compatible">
          <p>Hearing workflows require an active numbering policy for this location. Setup selects the active policy or creates one when needed. Save the draft to keep the selection.</p>
          <button uiButton type="button" [disabled]="authoringDisabled || !savedIssuingLocation"
            (click)="setUpHearingNumbering()">{{ configuring() ? 'Setting up hearing numbering…' : 'Set up hearing numbering' }}</button>
          <p *ngIf="!savedIssuingLocation">Save the hearing draft and its issuing location to enable setup.</p>
        </div>
        <div class="public-references__policy">
          <label>
            <span>Published numbering policy</span>
            <select uiControl aria-label="Published numbering policy"
              [ngModel]="selectedPolicyId" (ngModelChange)="change({ mode: 'required', policyId: $event || null })"
              [disabled]="authoringDisabled || loading() || !catalogLoaded()">
              <option value="">Choose a published policy</option>
              <option *ngIf="hasRetainedPolicyOption" [value]="selectedPolicyId">{{ retainedPolicyLabel }}</option>
              <option *ngFor="let policy of policies()" [value]="policy.id">{{ policy.code }} · revision {{ policy.revision }}</option>
            </select>
          </label>
          <button uiButton type="button" [text]="true" [outlined]="false"
            [disabled]="loading() || interactionBlocked || !compatible" (click)="load()">Refresh policies</button>
        </div>
        <p *ngIf="loading()" role="status" data-testid="numbering-catalog-loading">Loading published policies for this workflow and issuing location. The saved selection is retained.</p>
        <p *ngIf="catalogLoaded() && !policies().length" role="status" data-testid="numbering-catalog-empty">No published policies match this workflow and issuing location. Check the location or ask an administrator to publish a matching policy, then refresh.</p>
        <p *ngIf="unavailablePolicy" role="status" data-testid="numbering-policy-unavailable">The selected policy is unavailable in this catalog. Its identity is retained; the version's readiness and use of required references still need validation.</p>
        <p *ngIf="catalogLoaded() && policies().length && !selectedPolicyId" role="status">Choose an exact published policy before using required references. You can save the incomplete draft while resolving this prerequisite.</p>
      </ng-container>

      <p *ngIf="error()" class="public-references__notice" role="alert" data-testid="numbering-catalog-error">{{ error() }}</p>

      <div class="public-references__codes" *ngIf="agencyCodeLimit !== null || programCodeLimit !== null">
        <label *ngIf="agencyCodeLimit !== null">
          <span>Agency code</span>
          <input uiControl aria-label="Agency code" required [maxlength]="agencyCodeLimit"
            [ngModel]="document?.numbering?.agencyCode || ''"
            (ngModelChange)="change({ agencyCode: $event || null })" [disabled]="authoringDisabled" />
          <small>The selected format requires this code (up to {{ agencyCodeLimit }} characters).</small>
        </label>
        <label *ngIf="programCodeLimit !== null">
          <span>Program code</span>
          <input uiControl aria-label="Program code" required [maxlength]="programCodeLimit"
            [ngModel]="document?.numbering?.programCode || ''"
            (ngModelChange)="change({ programCode: $event || null })" [disabled]="authoringDisabled" />
          <small>The selected format requires this code (up to {{ programCodeLimit }} characters).</small>
        </label>
        <p>These codes are formatting inputs. They do not change the issuing location.</p>
      </div>

      <p *ngIf="contextMetadataUnavailable" role="status">The selected policy's format metadata is unavailable or unsupported. Saved context codes are retained. Refresh or restore the exact policy to edit the codes its format uses.</p>
      <details *ngIf="retainedAgencyCode || retainedProgramCode" class="public-references__retained">
        <summary>Retained context codes</summary>
        <p>These saved values are kept while their use cannot be confirmed or the selected format does not use them.</p>
        <dl>
          <div *ngIf="retainedAgencyCode"><dt>Saved agency code</dt><dd>{{ retainedAgencyCode }}</dd></div>
          <div *ngIf="retainedProgramCode"><dt>Saved program code</dt><dd>{{ retainedProgramCode }}</dd></div>
        </dl>
      </details>
      <details *ngIf="selectedPolicyId" class="public-references__retained">
        <summary>Exact policy identity</summary>
        <p>{{ selectedPolicyId }}</p>
      </details>
    </div>
  </fieldset>

  <p class="public-references__explanation">Saving settings does not issue, reserve or allocate a reference. References are issued when new runs start; existing issued references never change.</p>
</section>
